import React, { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getTPRMFindingsApi,
  tprmFindingActionApi,
} from "../../../services/axios/AuthApis/TPRMApis";
import TPRMResourceTable, { StatusBadge } from "./TPRMResourceTable";
import {
  displayTPRMValue,
  formatTPRMDate,
} from "./tprmPageUtils";

const FILTERS = [
  {
    id: "severity",
    definition: {
      variant: "select",
      label: "Severity",
      placeholder: "All severities",
      options: ["Low", "Medium", "High", "Critical"].map((value) => ({
        value,
        label: value,
      })),
    },
  },
  {
    id: "status",
    definition: {
      variant: "select",
      label: "Remediation status",
      placeholder: "All statuses",
      options: [
        "Open",
        "In Progress",
        "Pending Verification",
        "Resolved",
        "Accepted",
        "Closed",
      ].map((value) => ({ value, label: value })),
    },
  },
];

const RiskRegisterPage = () => {
  const [searchParams] = useSearchParams();
  const selectedFindingId = searchParams.get("finding_id") || "";
  const fetchPage = useCallback(
    (params) => getTPRMFindingsApi({
      ...params,
      ...(selectedFindingId ? { finding_id: selectedFindingId } : {}),
    }),
    [selectedFindingId],
  );
  const columns = useMemo(
    () => [
      {
        accessorKey: "finding_id",
        header: "Finding ID",
        size: 160,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "title",
        header: "Finding title",
        size: 220,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        id: "vendor",
        accessorFn: (finding) =>
          finding.vendor_details?.name ?? finding.vendor,
        header: "Vendor",
        size: 190,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "risk_category",
        header: "Category",
        size: 150,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "severity",
        header: "Severity",
        size: 130,
        cell: ({ getValue }) => <StatusBadge value={getValue()} />,
      },
      {
        accessorKey: "risk_score",
        header: "Risk score",
        size: 120,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "remediation_owner",
        header: "Remediation owner",
        size: 180,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "remediation_due_date",
        header: "Due date",
        size: 140,
        cell: ({ getValue }) => formatTPRMDate(getValue()),
      },
      {
        id: "status",
        accessorFn: (finding) => finding.remediation_status,
        header: "Remediation status",
        size: 175,
        cell: ({ getValue }) => <StatusBadge value={getValue()} />,
      },
    ],
    [],
  );

  const getActions = useCallback((finding, { canWrite }) => {
    if (!canWrite) return [];
    switch (finding.remediation_status) {
      case "Open":
        return [{ action: "start_remediation", label: "Start remediation" }];
      case "In Progress":
        return [{ action: "resolve", label: "Submit for verification" }];
      case "Pending Verification":
        return [{ action: "verify_remediation", label: "Verify remediation" }];
      case "Resolved":
        return [{ action: "reopen", label: "Reopen" }];
      default:
        return [];
    }
  }, []);

  const performAction = useCallback(
    (finding, item) => tprmFindingActionApi(finding.finding_id, item.action),
    [],
  );

  return (
    <TPRMResourceTable
      columns={columns}
      description={selectedFindingId
        ? `Showing Risk Register finding ${selectedFindingId}.`
        : "Track tenant-scoped findings and their remediation status."}
      fetchPage={fetchPage}
      filters={FILTERS}
      getActions={getActions}
      getRowId={(finding) => finding.finding_id}
      performAction={performAction}
      title="Risk Register"
    />
  );
};

export default RiskRegisterPage;
